/**
 * Life4Earth V2 — React hook for Impact Signature data.
 *
 * Loads aggregated tier levels and scores for a user.
 * Automatically refreshes when the impactSignature cache is invalidated
 * (e.g. after a role completion emits an impact event via SignalR).
 *
 * Pattern follows useApiProjectRoles.ts.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchImpactSummary } from "./impactSignature";
import type { ImpactSummaryResponse, TierLevel } from "./impactSignature";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

// ── Result shape ────────────────────────────────────────────

export interface UseApiImpactSignatureResult {
  /** Computed tier per dimension. */
  tiers: Record<string, TierLevel> | null;
  /** Raw cumulative scores per dimension. */
  scores: Record<string, number> | null;
  /** Total impact events for this user. */
  totalEvents: number;
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  /** Force a refetch (e.g. after a local mutation). */
  refetch: () => void;
}

// ── Hook ────────────────────────────────────────────────────

/**
 * Load impact signature data for a user.
 * Pass null/undefined userGuid to skip loading.
 */
export function useApiImpactSignature(
  userGuid: string | null | undefined
): UseApiImpactSignatureResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiImpactSignatureResult>({
    tiers: null,
    scores: null,
    totalEvents: 0,
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const cacheKey = userGuid
    ? `impactSignature:${userGuid}`
    : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !userGuid) return;

    try {
      const cached = await fetchForKey(
        cacheKey!,
        async (): Promise<ImpactSummaryResponse> => {
          return fetchImpactSummary(userGuid);
        },
        { force: true }
      );

      if (!mountedRef.current) return;

      setData({
        tiers: cached.tiers,
        scores: cached.scores,
        totalEvents: cached.totalEvents,
        loading: false,
        error: null,
        source: "api",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load impact data",
        source: "api",
        refetch,
      }));
    }
  }, [userGuid, cacheKey]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (userGuid) {
      refetch();
    } else {
      setData({
        tiers: null,
        scores: null,
        totalEvents: 0,
        loading: false,
        error: null,
        source: "empty",
        refetch,
      });
    }
    return () => {
      mountedRef.current = false;
    };
  }, [userGuid, refetch]);

  // Listen for cache invalidation events (SignalR-driven)
  useEffect(() => {
    if (!cacheKey) return;

    const handleCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === cacheKey) {
        refetch();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
  }, [cacheKey, refetch]);

  return data;
}
